import React, {useEffect} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IComboBoxOption,
    IconButton,
    Modal,
    SelectionMode,
    Separator,
    Stack,
    StackItem,
    Text
} from "@fluentui/react";
import {contentStyles, iconButtonStyles} from "./index";
import {useBranding} from "../../hooks/useBranding";
import {IGetApprovalListDetailResponse} from "../../services/Approval";


type Properties = {
    list: IComboBoxOption | null
    listDetails: IGetApprovalListDetailResponse[] | null
    onDismissModal: () => void
    showModal: boolean
}

export function ApprovalListView({
                                     list,
                                     listDetails,
                                     onDismissModal,
                                     showModal
                                 }: Properties) {

    const {t,} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()

    /**
     *
     */
    useEffect(() => {
        if (showModal) {

        }
    }, [showModal])


    /**
     * On Before dismiss
     */
    const onBeforeDismissEmailPreview = () => {
        onDismissModal()
    }

    /**
     * Renders a row or column with text content based on the provided item value.
     * If the item is null or empty, it displays a styled placeholder text. Otherwise, it displays the item content in a medium text style.
     *
     * @param {string|null} item The text content to be displayed. If null or empty, a placeholder is shown.
     * @return {Element} The rendered JSX element containing the styled text or placeholder content.
     */
    function RenderRowColumn(item: string | null) {
        return (
            <>
                {item === null || item.trim().length === 0 ?

                    <Text variant={"small"}
                          styles={{
                              root: {
                                  padding: "4px 8px",
                                  borderRadius: 8,
                                  fontStyle: 'italic',
                                  color: branding.theme.palette.neutralTertiaryAlt,
                                  backgroundColor: branding.theme.palette.neutralLighterAlt
                              }
                          }}
                          title={t('text.emptyColumn', {ns: 'approval'})}
                    >
                        {t('text.emptyColumn', {ns: 'approval'})}
                    </Text>

                    :
                    <Text
                        styles={{
                            root: {
                                width: '100%',
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }
                        }}
                        variant={"medium"}
                        title={item}
                    >
                        {item}
                    </Text>

                }
            </>
        )
    }

    const listColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.columns', {ns: 'approval', Name: "1"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column1)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column2',
            name: t('title.columns', {ns: 'approval', Name: "2"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column2)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column3',
            name: t('title.columns', {ns: 'approval', Name: "3"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column3)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column4',
            name: t('title.columns', {ns: 'approval', Name: "4"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column4)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column5',
            name: t('title.columns', {ns: 'approval', Name: "5"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column5)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column6',
            name: t('title.columns', {ns: 'approval', Name: "6"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column6)}
                    </Stack>
                </>
            }
        }
    ]

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerListPreview}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"}
                                  title={t('title.ListPreview', {ns: 'approval', Name: list?.text})}>
                                {t('title.ListPreview', {ns: 'approval', Name: list?.text})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onBeforeDismissEmailPreview}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1}
                                       styles={{root: {overflow: 'visible', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {listDetails === null || listDetails?.length === 0 ?

                                        <Stack styles={{
                                            root: {
                                                height: '100%',
                                                textAlign: 'center',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }
                                        }}>
                                            <Text variant={'small'} block>
                                                <Trans>
                                                    {t('text.emptyDetailList', {ns: 'approval'})}
                                                </Trans>
                                            </Text>
                                        </Stack>
                                        :
                                        <StackItem styles={{root: {paddingTop: 8}}}>
                                            <DetailsList
                                                items={listDetails ?? []}
                                                columns={listColumns}
                                                layoutMode={DetailsListLayoutMode.justified}
                                                selectionMode={SelectionMode.none}
                                                styles={{root: {minWidth: 840}}}
                                            />
                                        </StackItem>
                                    }

                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    <DefaultButton
                                        onClick={() => {
                                            onBeforeDismissEmailPreview()
                                        }}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>
        </>
    )
}


